// @ts-nocheck
/**
 * Quick Shop Alpine.js Component
 * Manages collection filtering, mobile sidebar toggle, and purchase options.
 * Syncs subscription state with cart drawer via events.
 */
document.addEventListener("alpine:init", () => {
  /**
   * @param {{sellingPlans: Array<{id: number, name: string}>, isWholesaleCustomer: boolean}} config
   */
  Alpine.data("quickShop", function ({ sellingPlans = [], isWholesaleCustomer = false }) {
    return {
      sellingPlans: [...sellingPlans],
      isWholesaleCustomer,
      
      // State
      activeCollection: "ALL",
      showMobileSidebar: false,
      isMobile: false,
      purchaseType: "one-time",

      /**
       * Initialize the component
       */
      init() {
        this.isMobile = window.innerWidth <= 750;
        
        // Listen for window resize
        window.addEventListener("resize", () => {
          this.isMobile = window.innerWidth <= 750;
        });

        // Listen for sync events from cart drawer
        window.addEventListener("cart-drawer-store:sync-subscription", this.handleSyncFromCartDrawer.bind(this));

        // Sync initial state from cart drawer store
        this.syncFromCartStore();

        console.log("QuickShop initialized. Mobile:", this.isMobile);
      },

      /**
       * Clean up event listeners
       */
      destroy() {
        window.removeEventListener("cart-drawer-store:sync-subscription", this.handleSyncFromCartDrawer);
      },

      /**
       * Handle sync event from cart drawer
       * @param {CustomEvent} event
       */
      handleSyncFromCartDrawer(event) {
        const { isSubscriptionActive, sellingPlan, source } = event.detail || {};
        
        // Ignore events dispatched by quick shop itself to prevent loops
        if (source === "quick-shop") return;
        
        // Update purchase type based on subscription state
        this.purchaseType = isSubscriptionActive ? "subscription" : "one-time";
        
        console.log("QuickShop synced from cart drawer:", { isSubscriptionActive, sellingPlan, source });
      },

      /**
       * Sync state from cartDrawerStore on init
       */
      syncFromCartStore() {
        const store = Alpine.store("cartDrawerStore");
        if (!store) return;

        // Check if there's an active selling plan
        if (store.sellingPlan) {
          this.purchaseType = "subscription";
        }
      },

      // ==================== GETTERS ====================

      /**
       * Get cart products from the store
       * @returns {Array}
       */
      get cartProducts() {
        const store = Alpine.store("cartDrawerStore");
        return store?.products || [];
      },

      /**
       * Get the current selling plan from the store
       * @returns {number|null}
       */
      get currentSellingPlan() {
        const store = Alpine.store("cartDrawerStore");
        return store?.sellingPlan || null;
      },

      /**
       * Check if cart is empty
       * @returns {boolean}
       */
      get isCartEmpty() {
        return this.cartProducts.length === 0;
      },

      /**
       * Check if cart has subscription-eligible products
       * @returns {boolean}
       */
      get hasSubscriptionProducts() {
        // Check if any products in cart have selling plans or if selling plans are available
        return this.cartProducts.some(item => {
          return item.selling_plan_allocation || 
                 (item.product && item.product.selling_plan_groups && item.product.selling_plan_groups.length > 0);
        }) || this.sellingPlans.length > 0;
      },

      /**
       * Calculate total prices for one-time and subscription
       * @returns {{otp: number, subscription: number}}
       */
      get totalPrices() {
        const totalPrices = {
          otp: 0,
          subscription: 0,
        };

        // Calculate base price (one-time purchase price)
        const otpPrice = this.cartProducts.reduce((total, item) => {
          // Use compare_at_price from selling plan allocation if available (original price)
          // Otherwise use original_line_price
          const price = item.selling_plan_allocation?.compare_at_price 
            ? item.selling_plan_allocation.compare_at_price * item.quantity
            : item.original_line_price;
          return total + price;
        }, 0);

        totalPrices.otp = otpPrice;
        // 10% discount for subscription
        totalPrices.subscription = Math.round(otpPrice * 0.9);

        return totalPrices;
      },

      // ==================== COLLECTION FILTERING ====================

      /**
       * Filter products by collection
       * @param {string} collectionHandle - Collection handle or 'ALL'
       */
      filterByCollection(collectionHandle) {
        this.activeCollection = collectionHandle || "ALL";
      },

      /**
       * Check if a product should be visible based on active collection
       * @param {string} productCollections - Comma-separated list of collection handles
       * @returns {boolean}
       */
      isProductVisible(productCollections) {
        if (this.activeCollection === "ALL") return true;
        
        const collections = productCollections.split(",").map(c => c.trim()).filter(Boolean);
        return collections.includes(this.activeCollection);
      },

      // ==================== PURCHASE OPTIONS ====================

      /**
       * Handle purchase option toggle (Buy Once / Subscribe)
       */
      onTogglePurchaseOption() {
        const isSubscription = this.purchaseType === "subscription";
        const store = Alpine.store("cartDrawerStore");
        
        if (!store) return;

        if (isSubscription) {
          // Get the first selling plan as default if none selected
          const defaultPlan = this.getDefaultSellingPlan();
          store.setSellingPlan(defaultPlan);
        } else {
          store.setSellingPlan(null);
        }

        // Dispatch sync event to cart drawer (with source to identify origin)
        window.dispatchEvent(
          new CustomEvent("cart-drawer-store:sync-subscription", {
            detail: {
              isSubscriptionActive: isSubscription,
              sellingPlan: isSubscription ? store.sellingPlan : null,
              source: "quick-shop",
            },
          })
        );
      },

      /**
       * Handle frequency/selling plan change
       * @param {Event} event
       */
      onChangeFrequency(event) {
        const store = Alpine.store("cartDrawerStore");
        if (!store || !(event.target instanceof HTMLSelectElement)) return;

        const planId = parseInt(event.target.value, 10) || null;
        store.setSellingPlan(planId);

        // Dispatch sync event to cart drawer (with source to identify origin)
        window.dispatchEvent(
          new CustomEvent("cart-drawer-store:sync-subscription", {
            detail: {
              isSubscriptionActive: true,
              sellingPlan: planId,
              source: "quick-shop",
            },
          })
        );
      },

      /**
       * Get default selling plan ID
       * @returns {number|null}
       */
      getDefaultSellingPlan() {
        const preselectedId = window.preselectedSellingPlan;

        if (preselectedId) {
          const found = this.sellingPlans.find(plan => plan.id === Number(preselectedId));
          if (found) return found.id;
        }

        return this.sellingPlans[0]?.id || null;
      },

      // ==================== UTILITY METHODS ====================

      /**
       * Format price in currency
       * @param {number} amount - Amount in cents
       * @returns {string}
       */
      formatMoney(amount) {
        if (amount == null || isNaN(amount)) return "£0.00";

        // Convert cents to pounds
        const value = amount / 100;

        return new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency: "GBP",
          minimumFractionDigits: 2,
        }).format(value);
      },
    };
  });
});
